import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2,
  Euro,
  TrendingUp,
  Filter,
  X,
  Building2,
  Users,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  HandCoins,
  PiggyBank,
} from 'lucide-react';
import { api } from '@/lib/api';
import type {
  AnalyticsFiltersData,
  OwnerPerformanceRow,
  PerformanceResult,
  PerformanceRow,
  PerformanceTotals,
  ModoAlicante,
} from '@/lib/types';
import { ZONE_LABELS, classNames } from '@/lib/utils';
import { yearRange } from '@/lib/analyticsDates';
import MultiSelect from '@/components/ui/MultiSelect';
import InfoTooltip from '@/components/ui/InfoTooltip';
import PanelFiltros, { SelectorPeriodo } from '@/components/analytics/PanelFiltros';
import FiltroAlicante from '@/components/analytics/FiltroAlicante';
// El color de la nota sale del mismo sitio que en Analytics > Reseñas: si un
// piso es verde allí y ámbar aquí, el gestor deja de fiarse de los dos sitios.
import { colorNota, fmt } from '@/components/reviews/shared';
import GraficasRendimiento from '@/components/analytics/GraficasRendimiento';

const MODEL_LABELS: Record<string, string> = {
  gestion: 'Gestión',
  explotacion: 'Explotación',
  explotacion_lcm: 'Expl. LCM',
  explotacion_sin_irpf: 'Expl. s/IRPF',
  rent2rent: 'R2R',
  ambos: 'Ambos',
};

const MODEL_COLORS: Record<string, string> = {
  gestion: 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  explotacion: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  explotacion_lcm: 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
  explotacion_sin_irpf: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  rent2rent: 'bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300',
};

const SERENO_EST_INFO =
  'Estimación de ingresos de Sereno según las fórmulas de liquidación: en gestión, comisión de gestión + comisión venta directa (5%) + renting (sin IVA); en explotación/LCM, la parte de Sereno del neto + renting; en rent2rent, neto Hostify menos la renta fija. No incluye limpieza ni gastos de lavandería/mantenimiento (cubren costes del servicio).';

const OWNER_EST_INFO =
  'Estimación de lo que recibe el propietario en el periodo según su modelo de contrato: en gestión, el saldo de la liquidación (tras comisiones, limpieza, renting e IVA); en explotación/LCM, la factura de alquiler (base + IVA − IRPF); en rent2rent, la renta fija prorrateada por meses (+IVA). Sin gastos de lavandería/mantenimiento.';

function fmtMoney(value: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

type SortKey =
  | 'property'
  | 'nota5'
  | 'modelo'
  | 'reservas'
  | 'noches'
  | 'ocupacion'
  | 'adr'
  | 'facturacion'
  | 'limpieza'
  | 'comisionCanal'
  | 'neto'
  | 'serenoEst'
  | 'ownerEst';

type OwnerSortKey =
  | 'ownerName'
  | 'modelo'
  | 'propiedades'
  | 'reservas'
  | 'noches'
  | 'ocupacion'
  | 'adr'
  | 'facturacion'
  | 'limpieza'
  | 'comisionCanal'
  | 'neto'
  | 'serenoEst'
  | 'ownerEst';

interface Sort<K extends string> {
  key: K;
  dir: 'asc' | 'desc';
}

interface Column<K extends string> {
  key: K;
  label: string;
  align: 'left' | 'right';
  info?: string;
}

const NOTA_INFO =
  'Nota media de las reseñas del alojamiento en escala de 5, con las de Booking convertidas (÷2). Es de TODO el histórico, no del periodo elegido arriba: una media de un trimestre se mueve con dos reseñas y dejaría de servir para comparar pisos entre sí. Entre paréntesis, sobre cuántas reseñas está calculada.';

const COLUMNS: Column<SortKey>[] = [
  { key: 'property', label: 'Propiedad', align: 'left' },
  { key: 'nota5', label: 'Nota', align: 'right', info: NOTA_INFO },
  { key: 'modelo', label: 'Modelo', align: 'left' },
  { key: 'reservas', label: 'Reservas', align: 'right' },
  { key: 'noches', label: 'Noches', align: 'right' },
  { key: 'ocupacion', label: 'Ocup.', align: 'right' },
  { key: 'adr', label: 'ADR', align: 'right' },
  { key: 'facturacion', label: 'Bruto', align: 'right' },
  { key: 'limpieza', label: 'Limpieza', align: 'right' },
  { key: 'comisionCanal', label: 'Com. canal', align: 'right' },
  { key: 'neto', label: 'Neto', align: 'right' },
  { key: 'serenoEst', label: 'Sereno (est.)', align: 'right', info: SERENO_EST_INFO },
  { key: 'ownerEst', label: 'Propietario (est.)', align: 'right', info: OWNER_EST_INFO },
];

const OWNER_COLUMNS: Column<OwnerSortKey>[] = [
  { key: 'ownerName', label: 'Propietario', align: 'left' },
  { key: 'modelo', label: 'Modelo', align: 'left' },
  { key: 'propiedades', label: 'Props.', align: 'right' },
  { key: 'reservas', label: 'Reservas', align: 'right' },
  { key: 'noches', label: 'Noches', align: 'right' },
  {
    key: 'ocupacion',
    label: 'Ocup.',
    align: 'right',
    info: 'Ocupación media de su cartera: noches vendidas sobre las noches disponibles de todas sus propiedades en el periodo.',
  },
  { key: 'adr', label: 'ADR', align: 'right' },
  { key: 'facturacion', label: 'Bruto', align: 'right' },
  { key: 'limpieza', label: 'Limpieza', align: 'right' },
  { key: 'comisionCanal', label: 'Com. canal', align: 'right' },
  { key: 'neto', label: 'Neto', align: 'right' },
  { key: 'serenoEst', label: 'Sereno (est.)', align: 'right', info: SERENO_EST_INFO },
  { key: 'ownerEst', label: 'Propietario (est.)', align: 'right', info: OWNER_EST_INFO },
];

/** Clave del grupo de propiedades sin propietario asignado en el CRM. */
const SIN_PROPIETARIO = '__sin_propietario__';

const EMPTY_TOTALS: PerformanceTotals = {
  reservas: 0, noches: 0, facturacion: 0, limpieza: 0, comisionCanal: 0, neto: 0, serenoEst: 0, ownerEst: 0,
};

const OWNER_VIEW_INFO =
  'Un propietario puede tener varias propiedades: aquí se suman sus métricas y las estimaciones se recalculan pasando todas sus reservas juntas por el motor de liquidaciones, que es como se liquida de verdad (una liquidación por propietario, no una por piso). Por eso el total puede no cuadrar al céntimo con la suma de sus propiedades.';

export default function PerformancePage() {
  const [filtersData, setFiltersData] = useState<AnalyticsFiltersData | null>(null);
  const [data, setData] = useState<PerformanceResult | null>(null);
  const [loading, setLoading] = useState(false);

  const initial = yearRange(new Date().getFullYear());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [zones, setZones] = useState<string[]>([]);
  const [includeOwner, setIncludeOwner] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  // Alicante fuera por defecto: es otra operativa y mezclarla distorsiona las
  // medias. Como «solo activas», cuenta como filtro al SALIRSE de ese estado.
  const [alicante, setAlicante] = useState<ModoAlicante>('sin');
  const [prorate, setProrate] = useState(true);
  const [vista, setVista] = useState<'propiedad' | 'propietario'>('propiedad');
  // «Ranking» es la tabla de siempre; «Gráficas», la lectura visual del mismo
  // periodo. Comparten datos y filtros: cambiar de subsección no pide nada al
  // servidor, así que el salto es instantáneo y las dos cuentan lo mismo.
  const [seccion, setSeccion] = useState<'ranking' | 'graficas'>('ranking');
  const [sort, setSort] = useState<Sort<SortKey>>({ key: 'facturacion', dir: 'desc' });
  const [ownerSort, setOwnerSort] = useState<Sort<OwnerSortKey>>({ key: 'facturacion', dir: 'desc' });
  const [expandedOwners, setExpandedOwners] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.getAnalyticsFilters().then(setFiltersData).catch(() => setFiltersData(null));
  }, []);

  const loadData = async (overrides?: { from?: string; to?: string; alicante?: ModoAlicante }) => {
    setLoading(true);
    try {
      const result = await api.getPerformance({
        from: overrides?.from ?? from,
        to: overrides?.to ?? to,
        zones,
        includeOwner,
        activeOnly,
        alicante: overrides?.alicante ?? alicante,
        prorate,
      });
      setData(result);
    } catch (e) {
      console.error(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zoneOptions = useMemo(
    () => (filtersData?.zones || []).map((z) => ({ value: z, label: ZONE_LABELS[z] || z })),
    [filtersData]
  );

  const sortedRows = useMemo(() => {
    const list = [...(data?.rows || [])];
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (key === 'property') return mult * a.property.localeCompare(b.property);
      if (key === 'modelo') return mult * String(a.modelo || '').localeCompare(String(b.modelo || ''));
      const av = a[key] ?? Number.NEGATIVE_INFINITY;
      const bv = b[key] ?? Number.NEGATIVE_INFINITY;
      return mult * ((av as number) - (bv as number));
    });
    return list;
  }, [data, sort]);

  /** Nota de toda la cartera visible, ponderada por reseñas. */
  const notaCartera = useMemo(() => {
    const filas = (data?.rows || []).filter((r) => r.nota5 !== null && r.nResenas > 0);
    if (!filas.length) return null;
    const n = filas.reduce((t, r) => t + r.nResenas, 0);
    return filas.reduce((t, r) => t + (r.nota5 as number) * r.nResenas, 0) / n;
  }, [data]);

  const sortedOwnerRows = useMemo(() => {
    const list = [...(data?.ownerRows || [])];
    const { key, dir } = ownerSort;
    const mult = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (key === 'ownerName') return mult * a.ownerName.localeCompare(b.ownerName);
      if (key === 'modelo') return mult * String(a.modelo || '').localeCompare(String(b.modelo || ''));
      const av = a[key] ?? Number.NEGATIVE_INFINITY;
      const bv = b[key] ?? Number.NEGATIVE_INFINITY;
      return mult * ((av as number) - (bv as number));
    });
    return list;
  }, [data, ownerSort]);

  // Detalle que se despliega bajo cada propietario: sus propiedades, ya
  // calculadas en la vista por propiedad.
  const rowsByOwner = useMemo(() => {
    const map = new Map<string, PerformanceRow[]>();
    for (const r of data?.rows || []) {
      const key = r.ownerId || SIN_PROPIETARIO;
      const list = map.get(key);
      if (list) list.push(r);
      else map.set(key, [r]);
    }
    for (const list of map.values()) list.sort((a, b) => b.facturacion - a.facturacion);
    return map;
  }, [data]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'property' || key === 'modelo' ? 'asc' : 'desc' }
    );
  };

  const toggleOwnerSort = (key: OwnerSortKey) => {
    setOwnerSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'ownerName' || key === 'modelo' ? 'asc' : 'desc' }
    );
  };

  const toggleOwnerExpanded = (key: string) => {
    setExpandedOwners((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // «Solo activas» es el estado por defecto: cuenta al APAGARSE. Imputar por
  // entrada también suma: cambia los datos y con el panel plegado nada más lo
  // delataría.
  const activeFilterCount =
    zones.length + (includeOwner ? 1 : 0) + (activeOnly ? 0 : 1) + (alicante === 'sin' ? 0 : 1) + (prorate ? 0 : 1);

  // Totales de la vista activa: las métricas coinciden en ambas, las
  // estimaciones no (se calculan por propiedad o por propietario).
  const totals = (vista === 'propietario' ? data?.ownerTotals : data?.totals) ?? EMPTY_TOTALS;

  return (
    <div>
      <PanelFiltros
        activos={activeFilterCount}
        barra={
          <SelectorPeriodo
            from={from}
            to={to}
            onChange={(r) => {
              setFrom(r.from);
              setTo(r.to);
              loadData({ from: r.from, to: r.to });
            }}
          />
        }
      >
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input min-h-11" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input min-h-11" />
          </div>
          <MultiSelect label="Zonas" icon={Filter} options={zoneOptions} selected={zones} onChange={setZones} allLabel="Todas" />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input type="checkbox" checked={includeOwner} onChange={(e) => setIncludeOwner(e.target.checked)} className="rounded" />
            Incluir estancias Owner
          </label>

          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} className="rounded" />
            Solo propiedades activas (PMS)
          </label>

          <FiltroAlicante valor={alicante} onChange={(next) => { setAlicante(next); loadData({ alicante: next }); }} />

          <div className="min-h-11 flex items-center gap-2">
            <span className="text-sm text-sereno-dark/60">Imputar por:</span>
            <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden">
              <button
                onClick={() => setProrate(true)}
                className={classNames(
                  'min-h-11 px-3 text-xs font-medium transition-colors',
                  prorate ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
                )}
              >
                Noche
              </button>
              <button
                onClick={() => setProrate(false)}
                className={classNames(
                  'min-h-11 px-3 text-xs font-medium transition-colors',
                  !prorate ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
                )}
              >
                Entrada
              </button>
            </div>
            <InfoTooltip text="Noche: el importe de cada reserva se reparte proporcionalmente entre las noches que caen dentro del periodo (devengo, igual que Hostify). Entrada: el importe completo se asigna al mes de check-in." />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button onClick={() => loadData()} disabled={loading} className="btn-primary min-h-11 flex items-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <TrendingUp size={16} />}
            Calcular
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setZones([]);
                setIncludeOwner(false);
                setActiveOnly(true);
                setAlicante('sin');
                setProrate(true);
              }}
              className="min-h-11 flex items-center gap-1.5 text-sm text-sereno-dark/50 hover:text-sereno-dark"
            >
              <X size={14} /> Limpiar filtros ({activeFilterCount})
            </button>
          )}
        </div>
      </PanelFiltros>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-sereno-dark/40" />
        </div>
      ) : !data ? (
        <div className="card text-center py-12 text-sereno-dark/50">No hay datos para el periodo seleccionado.</div>
      ) : (
        <>
          {/* KPIs — las estimaciones son las de la vista activa, que se calculan
              por propiedad o por propietario y pueden diferir por céntimos. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <KpiCard
              label="Facturación bruta"
              value={fmtMoney(totals.facturacion)}
              icon={Euro}
              color="bg-emerald-50 text-emerald-600"
              info="Alojamiento + limpieza + extras de todas las reservas del periodo, con IVA en todos los canales (el de Booking, que Hostify trae aparte, se suma)."
            />
            <KpiCard
              label="Neto (Hostify)"
              value={fmtMoney(totals.neto)}
              icon={Euro}
              color="bg-teal-50 text-teal-600"
              info="Ingresos netos estilo Hostify: subtotal menos comisiones de canal, tarifas y reembolsos."
            />
            <KpiCard
              label="Sereno (est.)"
              value={fmtMoney(totals.serenoEst)}
              icon={PiggyBank}
              color="bg-sky-50 text-sky-600"
              info={SERENO_EST_INFO}
            />
            <KpiCard
              label="Propietarios (est.)"
              value={fmtMoney(totals.ownerEst)}
              icon={HandCoins}
              color="bg-purple-50 text-purple-600"
              info={OWNER_EST_INFO}
            />
          </div>

          {/* Ranking o gráficas: el mismo periodo y los mismos filtros vistos
              como tabla o como dibujo. Botones y no pestañas con ruta porque no
              cambia lo que se pide al servidor, solo cómo se mira. */}
          <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden mb-4" role="group" aria-label="Subsección de Performance">
            <button
              onClick={() => setSeccion('ranking')}
              aria-pressed={seccion === 'ranking'}
              className={classNames(
                'inline-flex items-center gap-1.5 px-4 min-h-11 text-sm font-medium transition-colors',
                seccion === 'ranking' ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
              )}
            >
              <Building2 size={15} /> Ranking
            </button>
            <button
              onClick={() => setSeccion('graficas')}
              aria-pressed={seccion === 'graficas'}
              className={classNames(
                'inline-flex items-center gap-1.5 px-4 min-h-11 text-sm font-medium transition-colors',
                seccion === 'graficas' ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
              )}
            >
              <TrendingUp size={15} /> Gráficas
            </button>
          </div>

          {seccion === 'graficas' ? (
            <GraficasRendimiento rows={data.rows} />
          ) : (
          <div className="card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                {vista === 'propiedad' ? (
                  <Building2 size={16} className="text-sereno-dark/50" />
                ) : (
                  <Users size={16} className="text-sereno-dark/50" />
                )}
                <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
                  {vista === 'propiedad'
                    ? `Performance por propiedad (${data.rows.length})`
                    : `Performance por propietario (${data.ownerRows.length})`}
                </h2>
                <InfoTooltip
                  text={
                    vista === 'propiedad'
                      ? 'Las columnas Sereno (est.) y Propietario (est.) se calculan con el motor real de liquidaciones aplicado a las reservas del periodo, sin gastos de lavandería/mantenimiento ni detección de extensiones. Son estimaciones orientativas, no liquidaciones oficiales.'
                      : OWNER_VIEW_INFO
                  }
                />
              </div>

              {/* Cambio de vista: las mismas reservas agrupadas de otra forma,
                  sin volver a pedir datos al servidor. */}
              <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden">
                <button
                  onClick={() => setVista('propiedad')}
                  className={classNames(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                    vista === 'propiedad' ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
                  )}
                >
                  <Building2 size={14} /> Por propiedad
                </button>
                <button
                  onClick={() => setVista('propietario')}
                  className={classNames(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                    vista === 'propietario' ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
                  )}
                >
                  <Users size={14} /> Por propietario
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              {vista === 'propiedad' ? (
                <table className="w-full text-sm whitespace-nowrap">
                  <SortableHead columns={COLUMNS} sort={sort} onSort={toggleSort} />
                  <tbody>
                    {sortedRows.map((p) => (
                      <PropertyRow key={p.propertyId || p.property} row={p} />
                    ))}
                    {data.rows.length === 0 && (
                      <tr>
                        <td colSpan={COLUMNS.length} className="py-4 text-center text-sereno-dark/40">Sin datos</td>
                      </tr>
                    )}
                  </tbody>
                  {data.rows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-sereno-dark/20 font-bold">
                        <td className="py-2.5 px-2">Total ({data.rows.length})</td>
                        {/* Media ponderada por número de reseñas y no media de
                            medias: un piso con cuatro reseñas no puede pesar lo
                            mismo que uno con ciento veintiocho. */}
                        <td className="py-2.5 px-2 text-right">
                          {notaCartera === null ? (
                            <span className="text-sereno-dark/25">—</span>
                          ) : (
                            <span className={classNames('tabular-nums', colorNota(notaCartera))}>
                              {fmt(notaCartera, 2)}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-2" />
                        <td className="py-2.5 px-2 text-right">{data.totals.reservas}</td>
                        <td className="py-2.5 px-2 text-right">{data.totals.noches}</td>
                        <td className="py-2.5 px-2" />
                        <td className="py-2.5 px-2" />
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.totals.facturacion)}</td>
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.totals.limpieza)}</td>
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.totals.comisionCanal)}</td>
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.totals.neto)}</td>
                        <td className="py-2.5 px-2 text-right text-sky-700 dark:text-sky-300">{fmtMoney(data.totals.serenoEst)}</td>
                        <td className="py-2.5 px-2 text-right text-purple-700 dark:text-purple-300">{fmtMoney(data.totals.ownerEst)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              ) : (
                <table className="w-full text-sm whitespace-nowrap">
                  <SortableHead columns={OWNER_COLUMNS} sort={ownerSort} onSort={toggleOwnerSort} />
                  <tbody>
                    {sortedOwnerRows.map((o) => {
                      const key = o.ownerId || SIN_PROPIETARIO;
                      return (
                        <OwnerRow
                          key={key}
                          row={o}
                          properties={rowsByOwner.get(key) || []}
                          expanded={expandedOwners.has(key)}
                          onToggle={() => toggleOwnerExpanded(key)}
                        />
                      );
                    })}
                    {data.ownerRows.length === 0 && (
                      <tr>
                        <td colSpan={OWNER_COLUMNS.length} className="py-4 text-center text-sereno-dark/40">Sin datos</td>
                      </tr>
                    )}
                  </tbody>
                  {data.ownerRows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-sereno-dark/20 font-bold">
                        <td className="py-2.5 px-2">Total ({data.ownerRows.length})</td>
                        <td className="py-2.5 px-2" />
                        <td className="py-2.5 px-2 text-right">{data.rows.length}</td>
                        <td className="py-2.5 px-2 text-right">{data.ownerTotals.reservas}</td>
                        <td className="py-2.5 px-2 text-right">{data.ownerTotals.noches}</td>
                        <td className="py-2.5 px-2" />
                        <td className="py-2.5 px-2" />
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.ownerTotals.facturacion)}</td>
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.ownerTotals.limpieza)}</td>
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.ownerTotals.comisionCanal)}</td>
                        <td className="py-2.5 px-2 text-right">{fmtMoney(data.ownerTotals.neto)}</td>
                        <td className="py-2.5 px-2 text-right text-sky-700 dark:text-sky-300">{fmtMoney(data.ownerTotals.serenoEst)}</td>
                        <td className="py-2.5 px-2 text-right text-purple-700 dark:text-purple-300">{fmtMoney(data.ownerTotals.ownerEst)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Cabecera de tabla ordenable, compartida por las dos vistas para que ordenar
 * por propiedad o por propietario se sienta igual.
 */
function SortableHead<K extends string>({
  columns,
  sort,
  onSort,
}: {
  columns: Column<K>[];
  sort: Sort<K>;
  onSort: (key: K) => void;
}) {
  return (
    <thead>
      <tr className="text-left text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
        {columns.map((c) => (
          <th key={c.key} className={classNames('py-2 px-2 font-medium', c.align === 'right' ? 'text-right' : 'text-left')}>
            <span className={classNames('inline-flex items-center gap-1', c.align === 'right' && 'flex-row-reverse')}>
              <button
                onClick={() => onSort(c.key)}
                className={classNames(
                  'inline-flex items-center gap-1 hover:text-sereno-dark transition-colors',
                  c.align === 'right' && 'flex-row-reverse',
                  sort.key === c.key ? 'text-sereno-dark' : 'text-sereno-dark/50'
                )}
              >
                <span>{c.label}</span>
                {sort.key !== c.key ? (
                  <ArrowUpDown size={12} className="text-sereno-dark/30" />
                ) : sort.dir === 'asc' ? (
                  <ArrowUp size={12} />
                ) : (
                  <ArrowDown size={12} />
                )}
              </button>
              {c.info && <InfoTooltip text={c.info} />}
            </span>
          </th>
        ))}
      </tr>
    </thead>
  );
}

function PropertyRow({ row: p }: { row: PerformanceRow }) {
  return (
    <tr className="border-b border-sereno-dark/5 last:border-0">
      <td className="py-2 px-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="max-w-[220px] truncate" title={p.ownerName ? `${p.property} — ${p.ownerName}` : p.property}>
            {p.property}
          </span>
          {p.listingId && (
            <a
              href={`https://app.hostify.com/listings/view/${p.listingId}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`Ver listing ${p.listingId} en Hostify`}
              className="text-sereno-dark/30 hover:text-sereno-dark transition-colors"
            >
              <ExternalLink size={13} />
            </a>
          )}
        </span>
      </td>
      <td className="py-2 px-2 text-right">
        {p.nota5 === null ? (
          <span className="text-sereno-dark/25">—</span>
        ) : (
          // El nombre enlaza a Hostify y la nota a las reseñas de ese piso: son
          // los dos sitios a los que se sale desde esta tabla, y desde aquí se
          // sale a leer por qué el número es el que es.
          <Link
            to={`/reviews/l/${p.listingId ?? ''}`}
            className="inline-flex items-baseline gap-1 hover:underline"
            title={`Ver las ${p.nResenas} reseñas de ${p.property}`}
          >
            <span className={classNames('font-semibold tabular-nums', colorNota(p.nota5))}>
              {fmt(p.nota5, 2)}
            </span>
            <span className="text-[11px] text-sereno-dark/40 tabular-nums">({p.nResenas})</span>
          </Link>
        )}
      </td>
      <td className="py-2 px-2">
        {p.modelo ? (
          <span
            className={classNames(
              'inline-block px-1.5 py-0.5 rounded text-[11px] font-medium',
              MODEL_COLORS[p.modelo] || 'bg-sereno-blue/20 text-sereno-dark/70'
            )}
            title={p.ownerName}
          >
            {MODEL_LABELS[p.modelo] || p.modelo}
          </span>
        ) : (
          <span className="text-sereno-dark/30">—</span>
        )}
      </td>
      <td className="py-2 px-2 text-right text-sereno-dark/60">{p.reservas}</td>
      <td className="py-2 px-2 text-right text-sereno-dark/60">{p.noches}</td>
      <td className="py-2 px-2 text-right text-sereno-dark/60">{Math.round(p.ocupacion)}%</td>
      <td className="py-2 px-2 text-right text-sereno-dark/60">{fmtMoney(p.adr)}</td>
      <td className="py-2 px-2 text-right font-semibold">{fmtMoney(p.facturacion)}</td>
      <td className="py-2 px-2 text-right text-sereno-dark/60">{fmtMoney(p.limpieza)}</td>
      <td className="py-2 px-2 text-right text-sereno-dark/60">{fmtMoney(p.comisionCanal)}</td>
      <td className="py-2 px-2 text-right font-semibold">{fmtMoney(p.neto)}</td>
      <td className="py-2 px-2 text-right font-semibold text-sky-700 dark:text-sky-300">
        {p.serenoEst == null ? <span className="text-sereno-dark/30 font-normal">—</span> : fmtMoney(p.serenoEst)}
      </td>
      <td className="py-2 px-2 text-right font-semibold text-purple-700 dark:text-purple-300">
        {p.ownerEst == null ? <span className="text-sereno-dark/30 font-normal">—</span> : fmtMoney(p.ownerEst)}
      </td>
    </tr>
  );
}

/**
 * Una fila por propietario, con todas sus propiedades sumadas. Se despliega
 * para ver de qué piso sale cada euro sin cambiar de vista.
 */
function OwnerRow({
  row: o,
  properties,
  expanded,
  onToggle,
}: {
  row: OwnerPerformanceRow;
  properties: PerformanceRow[];
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={classNames('border-b border-sereno-dark/5', expanded && 'bg-sereno-blue/10')}>
        <td className="py-2 px-2">
          <span className="inline-flex items-center gap-1.5">
            <button
              onClick={onToggle}
              className="text-sereno-dark/40 hover:text-sereno-dark transition-colors"
              aria-label={expanded ? 'Ocultar propiedades' : 'Ver propiedades'}
              aria-expanded={expanded}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {o.ownerId ? (
              <Link
                to={`/owners/${o.ownerId}`}
                className="max-w-[240px] truncate font-medium hover:underline"
                title={`Ver ficha de ${o.ownerName}`}
              >
                {o.ownerName}
              </Link>
            ) : (
              <span className="max-w-[240px] truncate text-sereno-dark/50 italic" title="Listings sin propiedad o sin propietario asignado en el CRM">
                {o.ownerName}
              </span>
            )}
          </span>
        </td>
        <td className="py-2 px-2">
          {o.modelo ? (
            <span
              className={classNames(
                'inline-block px-1.5 py-0.5 rounded text-[11px] font-medium',
                MODEL_COLORS[o.modelo] || 'bg-sereno-blue/20 text-sereno-dark/70'
              )}
            >
              {MODEL_LABELS[o.modelo] || o.modelo}
            </span>
          ) : (
            <span className="text-sereno-dark/30">—</span>
          )}
        </td>
        <td className="py-2 px-2 text-right text-sereno-dark/60" title={o.propiedadesNombres.join(', ')}>
          {o.propiedades}
        </td>
        <td className="py-2 px-2 text-right text-sereno-dark/60">{o.reservas}</td>
        <td className="py-2 px-2 text-right text-sereno-dark/60">{o.noches}</td>
        <td className="py-2 px-2 text-right text-sereno-dark/60">{Math.round(o.ocupacion)}%</td>
        <td className="py-2 px-2 text-right text-sereno-dark/60">{fmtMoney(o.adr)}</td>
        <td className="py-2 px-2 text-right font-semibold">{fmtMoney(o.facturacion)}</td>
        <td className="py-2 px-2 text-right text-sereno-dark/60">{fmtMoney(o.limpieza)}</td>
        <td className="py-2 px-2 text-right text-sereno-dark/60">{fmtMoney(o.comisionCanal)}</td>
        <td className="py-2 px-2 text-right font-semibold">{fmtMoney(o.neto)}</td>
        <td className="py-2 px-2 text-right font-semibold text-sky-700 dark:text-sky-300">
          {o.serenoEst == null ? <span className="text-sereno-dark/30 font-normal">—</span> : fmtMoney(o.serenoEst)}
        </td>
        <td className="py-2 px-2 text-right font-semibold text-purple-700 dark:text-purple-300">
          {o.ownerEst == null ? <span className="text-sereno-dark/30 font-normal">—</span> : fmtMoney(o.ownerEst)}
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-sereno-dark/5 bg-sereno-blue/5">
          <td colSpan={OWNER_COLUMNS.length} className="px-2 pb-3 pt-1">
            <div className="pl-6 border-l-2 border-sereno-dark/10">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-sereno-dark/40">
                    <th className="py-1 pr-2 text-left font-medium">Propiedad</th>
                    <th className="py-1 px-2 text-right font-medium">Reservas</th>
                    <th className="py-1 px-2 text-right font-medium">Noches</th>
                    <th className="py-1 px-2 text-right font-medium">Bruto</th>
                    <th className="py-1 px-2 text-right font-medium">Neto</th>
                    <th className="py-1 px-2 text-right font-medium">Sereno (est.)</th>
                    <th className="py-1 pl-2 text-right font-medium">Propietario (est.)</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((p) => (
                    <tr key={p.propertyId || p.property} className="text-sereno-dark/70">
                      <td className="py-1 pr-2">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="max-w-[240px] truncate" title={p.property}>{p.property}</span>
                          {p.listingId && (
                            <a
                              href={`https://app.hostify.com/listings/view/${p.listingId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Ver listing ${p.listingId} en Hostify`}
                              className="text-sereno-dark/30 hover:text-sereno-dark transition-colors"
                            >
                              <ExternalLink size={11} />
                            </a>
                          )}
                        </span>
                      </td>
                      <td className="py-1 px-2 text-right">{p.reservas}</td>
                      <td className="py-1 px-2 text-right">{p.noches}</td>
                      <td className="py-1 px-2 text-right font-medium">{fmtMoney(p.facturacion)}</td>
                      <td className="py-1 px-2 text-right font-medium">{fmtMoney(p.neto)}</td>
                      <td className="py-1 px-2 text-right text-sky-700 dark:text-sky-300">
                        {p.serenoEst == null ? '—' : fmtMoney(p.serenoEst)}
                      </td>
                      <td className="py-1 pl-2 text-right text-purple-700 dark:text-purple-300">
                        {p.ownerEst == null ? '—' : fmtMoney(p.ownerEst)}
                      </td>
                    </tr>
                  ))}
                  {properties.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-1 text-sereno-dark/40">Sin propiedades en la vista</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Las estimaciones del propietario se calculan sobre todas sus
                  reservas juntas, así que la suma de estas filas puede quedar
                  a unos céntimos del total de arriba. */}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  info,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
  info?: string;
}) {
  return (
    <div className="card">
      <div className="flex items-start justify-between mb-2">
        <div className={classNames('p-2.5 rounded-lg', color)}>
          <Icon size={20} />
        </div>
        {info && <InfoTooltip text={info} />}
      </div>
      <p className="text-xs text-sereno-dark/50 truncate">{label}</p>
      <p className="text-lg font-bold truncate">{value}</p>
    </div>
  );
}
